import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";

export const FLOCK_NAME_MAX = 40;

// New flock / rename flock: one name field (the server cleans it and refuses duplicates and “Unsorted”).
export const FlockNameDialog = ({ open, onOpenChange, initial = "", title, cta, onSubmit }) => {
  const [name, setName] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setName(initial);
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    const ok = await onSubmit(name.trim());
    setBusy(false);
    if (ok) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="flock-dialog" className="sm:max-w-sm rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{title}</DialogTitle>
          <DialogDescription>A short name for a group of ducks, up to {FLOCK_NAME_MAX} characters.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-3">
          <Input autoFocus data-testid="flock-dialog-input" maxLength={FLOCK_NAME_MAX} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Christmas ducks" className="h-11 rounded-full" />
          <Button type="submit" data-testid="flock-dialog-submit" disabled={busy || !name.trim()} className="rounded-full h-11 font-bold gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {cta}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Deleting a flock never deletes a duck: they all go to Unsorted.
export const DeleteFlockDialog = ({ flock, onOpenChange, onConfirm }) => (
  <AlertDialog open={!!flock} onOpenChange={onOpenChange}>
    <AlertDialogContent data-testid="flock-delete-dialog" className="rounded-3xl">
      <AlertDialogHeader>
        <AlertDialogTitle>Delete “{flock?.name}”?</AlertDialogTitle>
        <AlertDialogDescription>
          Its {flock?.count ?? 0} duck{flock?.count === 1 ? "" : "s"} move to Unsorted. No duck is deleted.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="flock-delete-cancel" className="rounded-full">Keep it</AlertDialogCancel>
        <AlertDialogAction data-testid="flock-delete-confirm" className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={onConfirm}>
          Delete flock
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);
